import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { pickDataProvider } from '../helper/pickDataProvider';
import { ResourceContext } from '../Resource';
import { useResource } from './useResource';
import { useDataProvider } from './useDataProvider';

export const useInfiniteList = (options) => {
  const { resources } = React.useContext(ResourceContext);
  const dataProvider = useDataProvider();

  const {
    resource: resourceName,
    dataProvider: dataProviderName,
    pagination,
    filters,
    sorters,
    meta,
    skip,
  } = options;

  const [result, setResult] = useState({
    loaded: false,
    loading: false,
    error: null,
    data: {
      pages: [],
    },
    pagination: pagination || {
      current: 1,
      pageSize: 10,
    },
  });
  const resource = useResource({ resourceName });
  const pickedDataProvider = pickDataProvider(resourceName, dataProviderName, resources);
  if (!resource) {
    throw new Error(`Resource "${resourceName}" not found`);
  }
  const pickedMeta = {
    ...resource.meta,
    ...meta,
  };
  const { getList } = dataProvider(pickedDataProvider);

  const hasNextPage = useMemo(() => {
    if (!result.loaded) return false;
    if (!result.pagination.serverPagination) return false;
    if (result.pagination.serverPagination.pages === 0) return false;
    return result.pagination.current < result.pagination.serverPagination.pages;
  }, [result]);

  const fetchNextPage = () => {
    if (skip) return;
    setResult({
      ...result,
      pagination: {
        ...result.pagination,
      },
      loading: true,
    });
    getList({
      resource: resource.list || resource.name,
      pagination: {
        pageNo: result.pagination.current + 1,
        pageSize: result.pagination.pageSize,
      },
      filters,
      sorters,
      meta: pickedMeta,
    })
      .then(({ data }) => {
        const list = get(data, 'data.list', []);
        const total = get(data, 'data.pagination.total', 0);
        const serverPagination = get(data, 'data.pagination', {});
        const nextData = result.data;
        nextData.pages.push({ data: list });
        setResult({
          ...result,
          loaded: true,
          loading: false,
          data: nextData,
          total,
          pagination: {
            ...result.pagination,
            current: result.pagination.current + 1,
            serverPagination,
          },
        });
      })
      .catch((error) => {
        setResult({ ...result, loaded: false, loading: false, error });
      });
  };

  useEffect(() => {
    if (skip) return;
    if (!result.loaded && !result.loading) {
      setResult({ ...result, loading: true });
      getList({
        resource: resource.list || resource.name,
        pagination: {
          pageNo: result.pagination.current,
          pageSize: result.pagination.pageSize,
        },
        filters,
        sorters,
        meta: pickedMeta,
      })
        .then(({ data }) => {
          const list = get(data, 'data.list', []);
          const total = get(data, 'data.pagination.total', 0);
          const serverPagination = get(data, 'data.pagination', {});
          setResult({
            loaded: true,
            loading: false,
            data: {
              pages: [
                {
                  data: list,
                },
              ],
            },
            total,
            pagination: {
              ...result.pagination,
              serverPagination,
            },
          });
        })
        .catch((error) => {
          setResult({ loaded: false, loading: false, error });
        });
    }
  }, [skip]);

  useEffect(() => {
    if (skip) return;
    if (result.loaded && !result.loading) {
      setResult({
        ...result,
        loading: true,
        data: {
          pages: [],
        },
        total: 0,
        pagination: {
          current: 1,
          pageSize: result.pagination.pageSize,
        },
      });
      getList({
        resource: resource.list || resource.name,
        pagination: {
          pageNo: 1,
          pageSize: result.pagination.pageSize,
        },
        filters,
        sorters,
        meta: pickedMeta,
      })
        .then(({ data }) => {
          const list = get(data, 'data.list', []);
          const total = get(data, 'data.pagination.total', 0);
          const serverPagination = get(data, 'data.pagination', {});
          setResult({
            loaded: true,
            loading: false,
            data: {
              pages: [
                {
                  data: list,
                },
              ],
            },
            total,
            pagination: {
              current: 1,
              pageSize: result.pagination.pageSize,
              serverPagination,
            },
          });
        })
        .catch((error) => {
          setResult({ loaded: false, loading: false, error });
        });
    }
  }, [JSON.stringify(filters)]);

  return {
    fetchNextPage,
    hasNextPage,
    pagination: result.pagination,
    filters,
    sorters,
    meta: pickedMeta,
    ...result,
  };
};
